/* eslint-disable react/forbid-prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { CSVLink } from 'react-csv';
import { MdFileDownload } from 'react-icons/md';
import { toast } from 'react-toastify';

import { formatDistance } from 'date-fns';
import PropTypes from 'prop-types';

import { Button } from '@material-ui/core';

import DataTable from '~/components/DataTable';
import FormSwitch from '~/components/Form/Switch';
import LocaleMessage from '~/components/LocaleMessage';
import Splash from '~/components/Splash/Inside';

import api from '~/services/pluginbot-api';
import GetFileName from '~/util/GetFileName';

export default function SUApplications({
    requestError,
    renderDialog,
    filter,
    formats,
}) {
    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [currApp, setCurrApp] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const headersFile = [
        { key: 'active', label: 'Is Active' },
        { key: 'pluginspace_name', label: 'Pluginspace' },
        { key: 'group_name', label: 'Group' },
        { key: 'name', label: 'Name' },
        { key: 'type_name', label: 'App Type' },
        { key: 'robot_types', label: 'Robot Types' },
        {
            key: 'created_date',
            label: 'Creation Date',
        },
        {
            key: 'created_time',
            label: 'Creation Time',
        },
    ];

    const headerCells = [
        {
            id: 'pluginspace_name',
            label: <LocaleMessage msg="table.headers.pluginspace" />,
        },
        {
            id: 'name',
            label: <LocaleMessage msg="table.headers.name" />,
        },
        {
            id: 'type_name',
            label: <LocaleMessage msg="table.headers.app_type" />,
        },
        {
            id: 'robot_types',
            label: <LocaleMessage msg="table.headers.robot_type" />,
        },
        {
            id: 'status_name',
            label: <LocaleMessage msg="table.headers.status" />,
            order_by: 'active',
        },
        {
            id: 'created_date',
            label: <LocaleMessage msg="table.headers.created" />,
            order_by: 'created_timestamp',
        },
    ];

    function filterData(raw_data) {
        const data = raw_data.map(a => {
            const created = new Date(a.created);
            const updated = new Date(a.updated);
            const c_time = created.toLocaleTimeString([], formats.time);
            const u_time = updated.toLocaleTimeString([], formats.time);
            let r_types = '';
            const r_types_len = a.robot_types.length;
            a.robot_types.forEach((r, idx) => {
                r_types += `${r.name}${idx !== r_types_len - 1 ? ', ' : ''}`;
            });
            return {
                ...a,
                pluginspace_name:
                    a.pluginspace && a.pluginspace.name
                        ? a.pluginspace.name
                        : '---',
                group_name: a.group && a.group.name ? a.group.name : '---',
                type_name:
                    a.app_type && a.app_type.name ? a.app_type.name : '---',
                robot_types: r_types,
                status_name: a.active ? (
                    <LocaleMessage msg="info.status.active" />
                ) : (
                    <LocaleMessage msg="info.status.inactive" />
                ),
                created_date: created.toLocaleDateString(
                    formats.format,
                    formats.date
                ),
                created_time: c_time,
                created_timestamp: created.toISOString(),
                updated_distance: formatDistance(updated, new Date(), {
                    addSuffix: true,
                    locale: formats.locale,
                }),
                updated_date: updated.toLocaleDateString(
                    formats.format,
                    formats.date
                ),
                updated_time: u_time,
                updated_timestamp: updated.toISOString(),
            };
        });
        setItems(data);
    }

    async function loadApplications() {
        setIsLoading(true);
        const filter_url = filter ? `?pluginspace=${filter}` : '';
        await api
            .get(`admin/applications${filter_url}`)
            .then(response => {
                filterData(response.data);
                setTimeout(() => {
                    setIsLoading(false);
                }, 100);
            })
            .catch(error => requestError(error));
    }

    async function updateApplication(event) {
        event.preventDefault();
        setDialogOpen(false);
        setCurrApp(null);

        if (!currApp) {
            return toast.error(
                <LocaleMessage msg="errors.application_not_found" />
            );
        }
        setIsLoading(true);

        const data = { active: currApp.active || false };

        await api
            .put(`admin/applications/${currApp.id}`, data)
            .then(() => {
                toast.success(
                    <LocaleMessage msg="page.applications.form.update_success" />
                );
            })
            .catch(error => requestError(error));
        loadApplications();
        return setIsLoading(false);
    }

    useEffect(() => {
        loadApplications();
    }, [formats, filter]);

    function handleDialogClose(event) {
        event.preventDefault();
        setDialogOpen(false);
        setCurrApp(null);
    }

    function handleTableRowClick(event, id) {
        event.preventDefault();
        const application = items.find(a => {
            return a.id === id;
        });
        setCurrApp(application);
        setDialogOpen(true);
    }

    function buildExportButton() {
        return (
            <CSVLink
                data={items}
                headers={headersFile}
                filename={GetFileName(`admin_analytics_applications`, 'csv')}
                onClick={() => {
                    toast.info(<LocaleMessage msg="message.generating_file" />);
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<MdFileDownload style={{ color: '#fff' }} />}
                >
                    <span style={{ color: '#fff' }}>
                        <LocaleMessage msg="button.export_raw" />
                    </span>
                </Button>
            </CSVLink>
        );
    }

    function buildListView() {
        const rowActions = [];

        return (
            <div style={{ minHeight: '150px', width: '100%', padding: '15px' }}>
                <DataTable
                    headerColumns={headerCells}
                    data={items}
                    orderColumn="name"
                    rowActions={rowActions}
                    handleTableRowClick={(event, id) =>
                        handleTableRowClick(event, id)
                    }
                />
            </div>
        );
    }

    function buildConfirmDialog() {
        return renderDialog({
            open: dialogOpen,
            title: <LocaleMessage msg="page.su_groups.list.edit" />,
            content: (
                <div className="col-12 mb-3">
                    <FormSwitch
                        value={currApp.active || false}
                        onChange={event =>
                            setCurrApp({
                                ...currApp,
                                active: event.target.checked,
                            })
                        }
                        label={<LocaleMessage msg="info.status.active" />}
                    />
                </div>
            ),
            actions: (
                <>
                    <Button onClick={handleDialogClose} color="primary">
                        <LocaleMessage msg="button.cancel" />
                    </Button>
                    <Button
                        onClick={e => updateApplication(e)}
                        color="primary"
                        autoFocus
                    >
                        <LocaleMessage msg="button.edit" />
                    </Button>
                </>
            ),
        });
    }

    return isLoading ? (
        <Splash />
    ) : (
        <>
            {dialogOpen ? buildConfirmDialog() : null}
            <div style={{ padding: '15px' }}>{buildExportButton()}</div>
            <>{buildListView()}</>
        </>
    );
}

SUApplications.defaultProps = {
    filter: '',
    formats: {
        date: {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        },
        time: {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
        },
    },
};

SUApplications.propTypes = {
    filter: PropTypes.string,
    formats: PropTypes.object,
    requestError: PropTypes.func.isRequired,
    renderDialog: PropTypes.func.isRequired,
};
